// Transit and parking cost data from the spreadsheet
interface CityCosts {
  transitSingleTicket: number
  transitMonthlyPass: number
  microSingleRide: number
  microMonthlyPass: number
  parkingDay: number
  parkingMonth: number
  transitDailyAverageCost?: number
  transitMonthlyAverageCost?: number
  microDailyAverageCost?: number
  microMonthlyAverageCost?: number
  carDailyAverageCost?: number
  carMonthlyAverageCost?: number
}

// City-specific cost data
const cityCosts: Record<string, CityCosts> = {
  Houston: {
    transitSingleTicket: 1.25,
    transitMonthlyPass: 55,
    microSingleRide: 0, // Service doesn't exist
    microMonthlyPass: 0, // Service doesn't exist
    parkingDay: 15,
    parkingMonth: 125,
  },
  'Iowa City': {
    transitSingleTicket: 0,
    transitMonthlyPass: 0,
    microSingleRide: 0, // Service doesn't exist
    microMonthlyPass: 0, // Service doesn't exist
    parkingDay: 16,
    parkingMonth: 180,
  },
  Chicago: {
    transitSingleTicket: 2.5,
    transitMonthlyPass: 75,
    microSingleRide: 6.7,
    microMonthlyPass: 11.99,
    parkingDay: 40,
    parkingMonth: 400,
  },
  Boston: {
    transitSingleTicket: 2.4,
    transitMonthlyPass: 90,
    microSingleRide: 2.95,
    microMonthlyPass: 30.5,
    parkingDay: 38,
    parkingMonth: 520,
  },
  Columbus: {
    transitSingleTicket: 2,
    transitMonthlyPass: 62,
    microSingleRide: 4.75,
    microMonthlyPass: 18,
    parkingDay: 17,
    parkingMonth: 190,
  },
  Washington: {
    transitSingleTicket: 3.5,
    transitMonthlyPass: 144,
    microSingleRide: 4.5,
    microMonthlyPass: 25,
    parkingDay: 35,
    parkingMonth: 250,
  },
  Woodmere: {
    transitSingleTicket: 2.9,
    transitMonthlyPass: 132,
    microSingleRide: 0, // Service doesn't exist
    microMonthlyPass: 0, // Service doesn't exist
    parkingDay: 3,
    parkingMonth: 40,
  },
  Westminster: {
    transitSingleTicket: 3,
    transitMonthlyPass: 114,
    microSingleRide: 4,
    microMonthlyPass: 12,
    parkingDay: 5,
    parkingMonth: 55,
  },
  Golden: {
    transitSingleTicket: 3,
    transitMonthlyPass: 114,
    microSingleRide: 0, // Service doesn't exist
    microMonthlyPass: 0, // Service doesn't exist
    parkingDay: 10,
    parkingMonth: 220,
  },
  'Santa Barbara': {
    transitSingleTicket: 2.5,
    transitMonthlyPass: 80,
    microSingleRide: 8.7,
    microMonthlyPass: 32,
    parkingDay: 12,
    parkingMonth: 150,
  },
}

// Small cities list
const smallCities = [
  'Decatur',
  'Goddard',
  'Hingham',
  'Park',
  'Warrenton',
  'Garber',
  'Highmore',
  'Summit',
  'Abbott',
  'Alpine',
  'Andrews',
  'Anson',
  'Bernard',
  'Cisco',
  'Dawson',
  'Dike',
  'Eldorado',
  'Gail',
  'Goldthwaite',
  'Goliad',
  'Iraan',
  'Lopeno',
  'Mart',
  'Rogers',
  'Lehi',
  'Alton',
  'Chatham',
  'Scottsburg',
]

// Medium cities list
const mediumCities = [
  'Spokane',
  'Oakland',
  'Dover',
  'Lombard',
  'Union',
  'Monticello',
  'Colby',
  'Syracuse',
  'Chillicothe',
]

// Generic cost data
const genericCosts = {
  small: {
    transitSingleTicket: 1.5,
    transitMonthlyPass: 35,
    microSingleRide: 0, // Service doesn't exist
    microMonthlyPass: 0, // Service doesn't exist
    parkingDay: 3,
    parkingMonth: 60,
  },
  medium: {
    transitSingleTicket: 2.25,
    transitMonthlyPass: 65,
    microSingleRide: 3.5,
    microMonthlyPass: 25,
    parkingDay: 8,
    parkingMonth: 120,
  },
}

/**
 * Calculate daily and monthly average costs based on days in office
 * @param oneWayCost - Cost for one way trip
 * @param monthlyPassCost - Cost of monthly pass
 * @param daysInOffice - Number of days per week in office (2-5)
 * @returns Object with monthly cost and pass type flag
 */
function calculateAverageCosts(
  oneWayCost: number,
  monthlyPassCost: number,
  daysInOffice: number
) {
  // Daily cost = one way cost * 2 (round trip)
  const dailyCost = oneWayCost * 2

  // Monthly days in office = days per week * 4 weeks
  const monthlyDaysInOffice = daysInOffice * 4

  // Monthly cost without pass = daily cost * monthly days in office
  const monthlyCostWithoutPass = dailyCost * monthlyDaysInOffice

  // Monthly cost is the minimum of monthly pass or pay-per-ride
  const monthlyCost = Math.min(monthlyPassCost, monthlyCostWithoutPass)

  // Determine which pass type is better (monthly pass or daily tickets)
  const isMonthlyPassBetter = monthlyPassCost <= monthlyCostWithoutPass

  return {
    monthlyCost,
    isMonthlyPassBetter,
  }
}

/**
 * Calculate car parking costs based on days in office
 * @param parkingDayCost - Cost for daily parking
 * @param parkingMonthCost - Cost for monthly parking pass
 * @param daysInOffice - Number of days per week in office (2-5)
 * @returns Object with monthly cost and pass type flag
 */
function calculateCarCosts(
  parkingDayCost: number,
  parkingMonthCost: number,
  daysInOffice: number
) {
  // Daily cost = daily parking cost
  const dailyCost = parkingDayCost

  // Monthly days in office = days per week * 4 weeks
  const monthlyDaysInOffice = daysInOffice * 4

  // Monthly cost without pass = daily cost * monthly days in office
  const monthlyCostWithoutPass = dailyCost * monthlyDaysInOffice

  // Monthly cost is the minimum of monthly parking pass or pay-per-day
  const monthlyCost = Math.min(parkingMonthCost, monthlyCostWithoutPass)

  // Determine which pass type is better (monthly pass or daily parking)
  const isMonthlyPassBetter = parkingMonthCost <= monthlyCostWithoutPass

  return {
    monthlyCost,
    isMonthlyPassBetter,
  }
}

/**
 * Get transit and parking costs for a given city with calculated averages
 * @param cityName - The name of the city
 * @param daysInOffice - Number of days per week in office (2-5, defaults to 3)
 * @returns Object with all cost values including calculated averages and pass type flags
 */
export function getCityCosts(
  cityName: string,
  daysInOffice: number = 3
): {
  transit: number
  micromobility: number
  car: number
  transitPassType: 'monthly' | 'daily'
  micromobilityPassType: 'monthly' | 'daily'
  carPassType: 'monthly' | 'daily'
} {
  // Normalize city name for case-insensitive comparison
  const normalizedCityName = cityName.trim()

  // Validate days in office parameter
  const validDaysInOffice = Math.max(2, Math.min(5, daysInOffice))

  let cityCost: CityCosts

  // First, check if we have specific data for this city
  if (cityCosts[normalizedCityName]) {
    cityCost = cityCosts[normalizedCityName]
  }
  // Check if it's a small city
  else if (smallCities.includes(normalizedCityName)) {
    cityCost = genericCosts.small
  }
  // Check if it's a medium city
  else if (mediumCities.includes(normalizedCityName)) {
    cityCost = genericCosts.medium
  }
  // Default to medium city costs if no match found
  else {
    cityCost = genericCosts.medium
  }

  // Calculate average costs for transit
  const transitAverage = calculateAverageCosts(
    cityCost.transitSingleTicket,
    cityCost.transitMonthlyPass,
    validDaysInOffice
  )

  // Calculate average costs for micromobility
  const microAverage = calculateAverageCosts(
    cityCost.microSingleRide,
    cityCost.microMonthlyPass,
    validDaysInOffice
  )

  // Calculate average costs for car parking
  const carAverage = calculateCarCosts(
    cityCost.parkingDay,
    cityCost.parkingMonth,
    validDaysInOffice
  )

  return {
    transit: transitAverage.monthlyCost,
    micromobility: microAverage.monthlyCost,
    car: carAverage.monthlyCost,
    transitPassType: transitAverage.isMonthlyPassBetter ? 'monthly' : 'daily',
    micromobilityPassType: microAverage.isMonthlyPassBetter
      ? 'monthly'
      : 'daily',
    carPassType: carAverage.isMonthlyPassBetter ? 'monthly' : 'daily',
  }
}

// Export the interface for use in other files
export type { CityCosts }

export const getEmployeeSavings = (
  cost: number,
  reach: number,
  totalEmployees: number
) => {
  if (!cost) return 0
  return (((cost * 0.35 * reach * totalEmployees) / 100) * 12).toFixed(2)
}

export const getEmployerSavings = (
  cost: number,
  reach: number,
  totalEmployees: number
) => {
  if (!cost) return 0
  return (((cost * 0.0765 * reach * totalEmployees) / 100) * 12).toFixed(2)
}
